/**
 * Kişi state (v0.1.64): one GET /people/{id}/activity per opened person, kept by id; read
 * again on Tekrar dene. Read only; nothing here decides anything.
 */

import { useSyncExternalStore } from 'react';
import type { TwinApi } from '../api/client.js';
import type { PersonActivity } from '../api/people.js';
import { useStore } from './context.js';

export interface PersonState {
  view: PersonActivity | null;
  error: string | null;
  loading: boolean;
}

const IDLE: PersonState = Object.freeze({ view: null, error: null, loading: false });

export class PeopleStore {
  private readonly states = new Map<string, PersonState>();
  private readonly inFlight = new Map<string, Promise<void>>();
  private readonly listeners = new Set<() => void>();

  constructor(private readonly api: TwinApi) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** The state of one person; a stable object until it changes. */
  stateOf(id: string): PersonState {
    return this.states.get(id) ?? IDLE;
  }

  private set(id: string, patch: Partial<PersonState>): void {
    this.states.set(id, { ...this.stateOf(id), ...patch });
    for (const listener of this.listeners) listener();
  }

  /** Loads once per id; the screen calls it on mount. */
  ensureLoaded(id: string): Promise<void> {
    const current = this.stateOf(id);
    const running = this.inFlight.get(id);
    if (current.view !== null || running !== undefined) return running ?? Promise.resolve();
    return this.reload(id);
  }

  reload(id: string): Promise<void> {
    this.set(id, { loading: true });
    const run = (async () => {
      try {
        const view = await this.api.getPersonActivity(id);
        this.set(id, { view, error: null, loading: false });
      } catch (error) {
        this.set(id, { error: error instanceof Error ? error.message : String(error), loading: false });
      }
    })();
    this.inFlight.set(id, run);
    void run.finally(() => {
      if (this.inFlight.get(id) === run) this.inFlight.delete(id);
    });
    return run;
  }
}

export function usePerson(id: string): { store: PeopleStore; state: PersonState } {
  const store = useStore().people;
  const state = useSyncExternalStore(
    store.subscribe,
    () => store.stateOf(id),
    () => store.stateOf(id),
  );
  return { store, state };
}
